import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/apiClient.ts";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import PaymentEditModal from '../payments/PaymentEditModal';
import ErrorBoundary from '../common/ErrorBoundary';
import ReceiptModal from '../common/ReceiptModal';
import TableSearch from '../common/TableSearch';
import { Wallet, Printer } from 'lucide-react';
import { formatDate } from '../../utils/date';
import DateInput from '../common/DateInput';

/**
 * PaymentsPage.jsx — نسخة محدثة
 * - سلكتات بالأسماء من السيرفر (agents / supervisors / generals) عبر /payments/meta
 * - إعداد axios صحيح عبر VITE_API_BASE_URL أو القيمة الافتراضية http://localhost:8000/api/v1
 * - Interceptor يحقن Authorization + X-Tenant + X-Branch (+ X-Vault اختيارياً)
 */

// استخدام عميل API الموحد القادم من services/apiClient

// ➤ أدوات مساعدة — إرسال التاريخ فقط بدون وقت (YYYY-MM-DD 00:00:00)
function toBackendDateOnly(localValue) {
  if (!localValue) return "";
  const dt = new Date(localValue);
  if (isNaN(dt.getTime())) return localValue;
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  const y = dt.getFullYear();
  const m = pad(dt.getMonth() + 1);
  const d = pad(dt.getDate());
  return `${y}-${m}-${d} 00:00:00`;
}

function formatAmount(row) {
  // اختيار المبلغ غير الصفري: الدفع يستخدم amount_debit، الاستلام يستخدم amount_credit
  const debit = Number(row.amount_debit || 0);
  const credit = Number(row.amount_credit || 0);
  const val = debit > 0 ? debit : credit;
  return `${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع`;
}

function getRowAmount(row) {
  const debit = Number(row?.amount_debit || 0);
  const credit = Number(row?.amount_credit || 0);
  return debit > 0 ? debit : credit;
}

// تنسيق المبلغ بفواصل الآلاف للعرض
function formatWithCommas(val) {
  if (val === "" || val === null || val === undefined) return "";
  const str = String(val).replace(/,/g, "");
  const num = parseFloat(str);
  if (isNaN(num)) return str;
  const parts = num.toFixed(2).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts[1] === "00" ? parts[0] : parts.join(".");
}

function parseFormattedNumber(str) {
  if (!str || str === "") return "";
  const cleaned = String(str).replace(/,/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? "" : num;
}

// اسم المرجع: وكيل أو مشرف أو عام من الميتا (عمود واحد يجمع الثلاثة)
function getRefName(row, meta) {
  if (!row || !meta) return "-";
  if (row.paytype === "Agent" && row.ag_id) {
    const a = (meta.agents || []).find((x) => Number(x.id) === Number(row.ag_id));
    return a?.name ?? row.ag_id;
  }
  if (row.paytype === "Supervisor" && row.su_id) {
    const s = (meta.supervisors || []).find((x) => Number(x.id) === Number(row.su_id));
    return s?.name ?? row.su_id;
  }
  if (row.paytype === "General" && row.ge_id) {
    const g = (meta.generals || []).find((x) => Number(x.id) === Number(row.ge_id));
    return g?.name ?? g?.title ?? row.ge_id;
  }
  return "-";
}

export default function PaymentsPage() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n?.language?.startsWith('ar');
  const creationDisabled = false;
  const initialForm = {
    actiontype: "Pay",
    paytype: "Agent",
    ag_id: "",
    su_id: "",
    ge_id: "",
    amount: "",
    notes: "",
    tx_date_local: "",
  };
  const [meta, setMeta] = useState({
    actiontypes: ["Pay", "Receive"],
    paytypes: ["Agent", "Supervisor", "General"],
    agents: [],
    supervisors: [],
    generals: [],
  });

  const [filters, setFilters] = useState({
    paytype: "",
    actiontype: "",
    ref_id: "", // وكيل أو مشرف أو عام حسب paytype — فارغ = الكل
    vault: "",
    from: "",
    to: "",
    per_page: 10,
  });

  // البحث يجري على الخادم (الجدول مُرقّم) فيغطي كل السجلات لا الصفحة الظاهرة
  const [search, setSearch] = useState("");

  const [form, setForm] = useState(initialForm);

  const [list, setList] = useState({ data: [], current_page: 1, last_page: 1, total: 0 });
  const [overallAmountTotal, setOverallAmountTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modals state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTargetId, setEditTargetId] = useState(null);

  // وصل الصندوق — يُعرض بعد الحفظ، وكذلك عند إعادة الطباعة من الجدول
  const [receipt, setReceipt] = useState(null);

  // ➤ تحميل الميتا (ثوابت + القوائم)
  useEffect(() => {
    let mounted = true;
    api
      .get("/v1/payments/meta")
      .then(({ data }) => {
        if (!mounted) return;
        setMeta((prev) => ({
          ...prev,
          actiontypes: data.actiontypes || prev.actiontypes,
          paytypes: data.paytypes || prev.paytypes,
          agents: data.agents || [],
          supervisors: data.supervisors || [],
          generals: data.generals || [],
        }));
      })
      .catch((e) => {
        console.warn("Failed to load meta:", e?.response?.data || e.message);
      });
    return () => (mounted = false);
  }, []);

  const buildFilterParams = (page = 1) => {
    const { ref_id, ...rest } = filters;
    const params = { ...rest, page };
    if (ref_id && filters.paytype === "Agent") params.ag_id = ref_id;
    if (ref_id && filters.paytype === "Supervisor") params.su_id = ref_id;
    if (ref_id && filters.paytype === "General") params.ge_id = ref_id;
    // يمر على القائمة والملخّص معاً فيبقى مجموع الفوتر مطابقاً للنتائج
    if (search.trim()) params.q = search.trim();
    return params;
  };

  // ➤ جلب القائمة — ref_id يتحول إلى ag_id / su_id / ge_id حسب paytype؛ إن كان فارغاً لا يُرسل (الكل)
  const fetchPayments = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const params = buildFilterParams(page);
      const [listResp, summaryResp] = await Promise.all([
        api.get("/v1/payments", { params }),
        api.get("/v1/payments/summary", { params }),
      ]);
      setList(listResp.data);
      setOverallAmountTotal(Number(summaryResp?.data?.total_amount || 0));
    } catch (e) {
      setError(t('common.error'));
      toast.error(e?.response?.data?.message || e.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // إعادة الجلب بعد توقّف الكتابة، لا مع كل حرف. يتخطّى أول تصيير
  // لأن مؤثّر التحميل أعلاه جلب الصفحة الأولى بالفعل.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }
    const timer = setTimeout(() => fetchPayments(1), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  /**
   * يبني وصل الصندوق من سجل دفعة كما يعيده الـ API.
   * يخدم الحالتين: السجل الجديد بعد الحفظ، وأي صف قديم من الجدول.
   * الوصل مكتوب بالعربية دائماً لأنه مستند رسمي محلي يُوقّع ويُختم.
   */
  const buildCashboxReceipt = (row) => {
    if (!row) return null;
    const isPay = row.actiontype === 'Pay';
    const partyTypeLabel =
      row.paytype === 'Agent' ? 'وكيل'
        : row.paytype === 'Supervisor' ? 'مشرف'
          : 'مصاريف عامة';

    const debit = Number(row.amount_debit || 0);
    const credit = Number(row.amount_credit || 0);
    const amount = debit > 0 ? debit : credit;

    return {
      direction: isPay ? 'out' : 'in',
      docTitle: isPay ? 'سند صرف' : 'سند قبض',
      subtitle: isPay ? 'صرف من الصندوق' : 'استلام في الصندوق',
      serial: row.id ? `${isPay ? 'PAY' : 'RCV'}-${String(row.id).padStart(6, '0')}` : '—',
      date: row.created_at,
      partyLabel: isPay ? 'صرفنا إلى السيد/السيدة' : 'استلمنا من السيد/السيدة',
      partyName: getRefName(row, meta),
      details: [
        { label: 'نوع العملية', value: isPay ? 'دفع (صرف)' : 'استلام (قبض)' },
        { label: 'جهة التعامل', value: partyTypeLabel },
        { label: 'الصندوق', value: row.vault ?? '—' },
        { label: 'رقم القيد', value: row.id ?? '—' },
      ],
      amount,
      notes: row.notes,
    };
  };

  // ➤ إرسال إنشاء سجل
  const onSubmit = async (e) => {
    e.preventDefault();
    if (creationDisabled) {
      toast.warn(t('payments.creationDisabled'));
      return;
    }
    // minimal validations
    const amountValue = Number(form.amount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast.error(t('payments.validation.totalAmountRequired'));
      return;
    }
    const refKey = form.paytype === 'Agent' ? 'ag_id' : form.paytype === 'Supervisor' ? 'su_id' : 'ge_id';
    if (!form[refKey]) {
      toast.error(t('travelers.validationRequired'));
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        actiontype: form.actiontype,
        paytype: form.paytype,
        amount: amountValue,
        notes: form.notes || '',
        tx_date: toBackendDateOnly(form.tx_date_local),
        ag_id: form.ag_id || undefined,
        su_id: form.su_id || undefined,
        ge_id: form.ge_id || undefined,
      };
      const resp = await api.post('/v1/payments', payload);
      const created = resp?.data?.data;
      if (created) setReceipt(buildCashboxReceipt(created));
      setSuccess(t('common.success'));
      toast.success(t('common.success'));
      setForm(initialForm);
      fetchPayments(list.current_page || 1);
    } catch (e) {
      setError(t('common.error'));
      toast.error(e?.response?.data?.message || e.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // ➤ حذف سجل عبر مودال تأكيد
  const onDeleteRequest = (id) => {
    setDeleteTargetId(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/v1/payments/${deleteTargetId}`);
      setSuccess(t('common.success'));
      toast.success(t('common.success'));
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
      fetchPayments(list.current_page || 1);
    } catch (e) {
      setError(t('common.error'));
      toast.error(e?.response?.data?.message || e.message || t('common.error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setDeleteTargetId(null);
  };

  // ➤ تجميعة الحقول حسب نوع الدفع
  const refLabel = useMemo(() => {
    switch (form.paytype) {
      case "Agent":
        return { key: "ag_id", label: t('travelers.agentName') };
      case "Supervisor":
        return { key: "su_id", label: t('payments.paytypeSupervisor') };
      case "General":
      default:
        return { key: "ge_id", label: t('payments.generalExpenses') };
    }
  }, [form.paytype, t]);

  const setRefValue = (v) => {
    if (refLabel.key === "ag_id") setForm((p) => ({ ...p, ag_id: v, su_id: "", ge_id: "" }));
    if (refLabel.key === "su_id") setForm((p) => ({ ...p, su_id: v, ag_id: "", ge_id: "" }));
    if (refLabel.key === "ge_id") setForm((p) => ({ ...p, ge_id: v, ag_id: "", su_id: "" }));
  };

  // مصادر الخيارات حسب النوع
  const currentOptions = useMemo(() => {
    if (form.paytype === "Agent") return meta.agents || [];
    if (form.paytype === "Supervisor") return meta.supervisors || [];
    return meta.generals || [];
  }, [form.paytype, meta]);

  return (
    <div className="text-gray-900 dark:text-gray-100 py-6 px-4 sm:px-6 lg:px-8" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="w-full mx-auto max-w-6xl">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-reverse space-x-3">
                <div className="bg-white/20 rounded-full p-3">
                  <Wallet className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-white">{t('navigation.PaymentsPage')}</h1>
                  <p className="text-blue-100 mt-1">حركة الصندوق وإدارة الدفعات والاستلامات</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6 space-y-6">
        {/* status messages */}
        {(error || success) && (
          <div className="mb-4 space-y-2">
            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-red-800">{error}</div>}
            {success && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-emerald-800">{success}</div>
            )}
          </div>
        )}
        {/* Form */}
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-4 rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6 shadow-sm" aria-disabled={creationDisabled}>
          {/* actiontype */}
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">{t('payments.actionType')}</label>
            <select
              className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              value={form.actiontype}
              onChange={(e) => setForm((p) => ({ ...p, actiontype: e.target.value }))}
            >
              {meta.actiontypes?.map((atype) => (
                <option key={atype} value={atype}>{atype === "Pay" ? t('payments.actionPay') : t('payments.actionReceive')}</option>
              ))}
            </select>
          </div>

          {/* paytype */}
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">{t('payments.paymentType')}</label>
            <select
              className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              value={form.paytype}
              onChange={(e) => setForm((p) => ({ ...p, paytype: e.target.value }))}
            >
              {meta.paytypes?.map((pt) => (
                <option key={pt} value={pt}>{pt === "Agent" ? t('payments.paytypeAgent') : pt === "Supervisor" ? t('payments.paytypeSupervisor') : t('payments.generalExpenses')}</option>
              ))}
            </select>
          </div>

          {/* ref select */}
          <div className="md:col-span-3">
            <label className="block text-sm mb-1">{refLabel.label}</label>
            <select
              className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              value={refLabel.key === "ag_id" ? form.ag_id : refLabel.key === "su_id" ? form.su_id : form.ge_id}
              onChange={(e) => setRefValue(e.target.value)}
            >
              <option value="">{t('common.select')}</option>
              {currentOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.name ?? opt.title ?? opt.label ?? opt.id}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">{t('payments.metaLoadedHint')}</p>
          </div>

          {/* amount */}
          <div className="md:col-span-2">
            <label className="block text-sm mb-1">{t('common.amount')}</label>
            <input
              type="text"
              inputMode="numeric"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              placeholder="0.00"
              value={formatWithCommas(form.amount)}
              onChange={(e) => {
                const parsed = parseFormattedNumber(e.target.value);
                setForm((p) => ({ ...p, amount: parsed === "" ? "" : parsed }));
              }}
              required
            />
          </div>

          {/* date */}
          <div className="md:col-span-3">
            <label className="block text-sm mb-1">{t('common.date')}</label>
            <DateInput
              className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              value={form.tx_date_local}
              onChange={(e) => setForm((p) => ({ ...p, tx_date_local: e.target.value }))}
            />
          </div>

          {/* notes */}
          <div className="md:col-span-12">
            <label className="block text-sm mb-1">{t('payments.notes')}</label>
            <textarea
              rows={2}
              className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              placeholder={t('payments.enterNotes')}
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            />
          </div>

          <div className="md:col-span-12 flex items-center gap-3 justify-end">
            <button type="button" onClick={() => fetchPayments(1)} className="rounded-xl border border-gray-200 dark:border-gray-600 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700">
              {t('common.refresh')}
            </button>
            <button type="submit" disabled={creationDisabled || submitting} className="rounded-xl bg-blue-600 px-5 py-2.5 text-white hover:bg-blue-700 disabled:opacity-60 shadow-sm" title={creationDisabled ? t('common.warning') : t('common.submit')}>
              {creationDisabled ? t('common.save') : (submitting ? t('common.loading') : t('common.save'))}
            </button>
          </div>
        </form>
        {/* فلاتر القائمة */}
        <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('payments.actionType')}</label>
              <select className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 dark:bg-gray-700 dark:text-gray-100" value={filters.actiontype} onChange={(e) => setFilters((p) => ({ ...p, actiontype: e.target.value }))}>
                <option value="">{t('common.all')}</option>
                {meta.actiontypes?.map((atype) => (
                  <option key={atype} value={atype}>{atype === "Pay" ? t('payments.actionPay') : t('payments.actionReceive')}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('payments.paymentType')}</label>
              <select
                className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 dark:bg-gray-700 dark:text-gray-100"
                value={filters.paytype}
                onChange={(e) => setFilters((p) => ({ ...p, paytype: e.target.value, ref_id: "" }))}
              >
                <option value="">{t('common.all')}</option>
                {meta.paytypes?.map((pt) => (
                  <option key={pt} value={pt}>{pt === "Agent" ? t('payments.paytypeAgent') : pt === "Supervisor" ? t('payments.paytypeSupervisor') : t('payments.generalExpenses')}</option>
                ))}
              </select>
            </div>

            {/* الاسم: قائمة وكيل أو مشرف أو عام حسب نوع الدفع — فارغ = الكل */}
            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('common.name')}</label>
              <select
                className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 dark:bg-gray-700 dark:text-gray-100"
                value={filters.ref_id}
                onChange={(e) => setFilters((p) => ({ ...p, ref_id: e.target.value }))}
                disabled={!filters.paytype}
              >
                <option value="">{t('common.all')}</option>
                {filters.paytype === "Agent" && (meta.agents || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name ?? opt.id}</option>
                ))}
                {filters.paytype === "Supervisor" && (meta.supervisors || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name ?? opt.id}</option>
                ))}
                {filters.paytype === "General" && (meta.generals || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name ?? opt.title ?? opt.id}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('common.fromDate')}</label>
              <DateInput className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 dark:bg-gray-700 dark:text-gray-100" value={filters.from} onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))} />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('common.toDate')}</label>
              <DateInput className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 dark:bg-gray-700 dark:text-gray-100" value={filters.to} onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))} />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm mb-1">{t('common.perPage')}</label>
              <select className="w-full rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2.5 dark:bg-gray-700 dark:text-gray-100" value={filters.per_page} onChange={(e) => setFilters((p) => ({ ...p, per_page: Number(e.target.value) }))}>
                {[10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 flex items-end">
              <button type="button" onClick={() => fetchPayments(1)} className="w-full rounded-xl bg-slate-800 px-4 py-2.5 text-white hover:bg-slate-900 shadow-sm">
                {t('common.submit')}
              </button>
            </div>
          </div>
        </div>

        {/* البحث فوق الجدول */}
        <TableSearch
          value={search}
          onChange={setSearch}
          placeholder="ابحث بالاسم أو الملاحظات أو رقم السند..."
          resultCount={search.trim() ? (list.total ?? 0) : undefined}
          hint="البحث يشمل كل السجلات لا الصفحة الظاهرة فقط"
        />

        {/* الجدول */}
        <div className="app-table-wrap shadow-sm">
          <table className="app-table min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">{t('common.index')}</th>
                <th className="px-3 py-2 text-left">{t('common.date')}</th>
                <th className="px-3 py-2 text-left">{t('payments.actionType')}</th>
                <th className="px-3 py-2 text-left">{t('payments.paymentType')}</th>
                <th className="px-3 py-2 text-left">{t('common.name')}</th>
                <th className="px-3 py-2 text-left">{t('common.amount')}</th>
                <th className="px-3 py-2 text-left">{t('common.notes')}</th>
                <th className="px-3 py-2 text-left">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-gray-500">{t('common.loading')}</td>
                </tr>
              ) : list.data?.length ? (
                list.data.map((row, idx) => (
                    <tr key={row.id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-3 py-2">{(list.current_page - 1) * (list.per_page || 10) + idx + 1}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.created_at)}</td>
                      <td className="px-3 py-2">{row.actiontype === "Pay" ? t('payments.actionPay') : t('payments.actionReceive')}</td>
                      <td className="px-3 py-2">{row.paytype === "Agent" ? t('payments.paytypeAgent') : row.paytype === "Supervisor" ? t('payments.paytypeSupervisor') : t('payments.generalExpenses')}</td>
                      <td className="px-3 py-2">{getRefName(row, meta)}</td>
                      <td className="px-3 py-2">{formatAmount(row)}</td>
                      <td className="px-3 py-2 max-w-[200px]" title={row.notes || ""}>{row.notes || ""}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            title="طباعة وصل الصندوق"
                            className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                            onClick={() => setReceipt(buildCashboxReceipt(row))}
                          >
                            <Printer className="h-4 w-4" />
                            وصل
                          </button>
                          <button className="rounded-lg border px-3 py-1 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => { setEditTargetId(row.id); setEditModalOpen(true); }}>{t('common.edit')}</button>
                          <button className="rounded-lg bg-red-600 px-3 py-1 text-white hover:bg-red-700" onClick={() => onDeleteRequest(row.id)}>{t('common.delete')}</button>
                        </div>
                      </td>
                    </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-gray-500">{t('common.noResults')}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} className="px-3 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">
                  إجمالي المبالغ (كل النتائج)
                </td>
                <td className="px-3 py-3 font-bold text-blue-700 dark:text-blue-300">
                  {overallAmountTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                </td>
                <td colSpan={2} className="px-3 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* ترقيم الصفحات */}
        <div className="mt-3 flex items-center justify-between text-sm">
          <div className="text-gray-600">{t('common.total')}: {list.total || 0}</div>
          <div className="flex items-center gap-2">
            <button className="rounded-lg border px-3 py-1 disabled:opacity-50" disabled={(list.current_page || 1) <= 1} onClick={() => fetchPayments((list.current_page || 1) - 1)}>{t('common.previous')}</button>
            <span>{t('common.page')} {(list.current_page || 1)} / {(list.last_page || 1)}</span>
            <button className="rounded-lg border px-3 py-1 disabled:opacity-50" disabled={(list.current_page || 1) >= (list.last_page || 1)} onClick={() => fetchPayments((list.current_page || 1) + 1)}>{t('common.next')}</button>
          </div>
        </div>
          </div>
        </div>
      {/* Modals */}
      <ErrorBoundary fallback={null}>
        <ConfirmDeleteModal
          open={deleteModalOpen}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          loading={deleteLoading}
          recordLabel={`${t('common.delete')} #${deleteTargetId ?? ''}`}
        />
        <PaymentEditModal
          open={editModalOpen}
          recordId={editTargetId}
          onClose={() => { setEditModalOpen(false); setEditTargetId(null); }}
          onSaved={() => fetchPayments(list.current_page || 1)}
        />
        <ReceiptModal
          open={!!receipt}
          receipt={receipt}
          onClose={() => setReceipt(null)}
        />
      </ErrorBoundary>
      </div>
    </div>
  );
}
